// Cartoon character in the real world's form (spec G14-cartoon-in-the-real-world, PR d). The
// playbook is its spec and live has no widget, so this form has no live capture (Cartoon-1). It is
// built on the kit of Flat cartoon's form (`FlatCartoonForm.tsx`), from the playbook's Fill in
// (L1547-1553), in two approvals. The script stage picks the product, takes the brand as said, the
// product's size and the surface, then writes the eleven lines, the eleven shots and the cast (the
// server's pack reader and director, or the member) and approves them. The cast stage shows the
// sixteen images (`CartoonRealWorldCast.tsx`). The narrator's voice is the member's pick (Cartoon-10),
// from Voice Settings, kept by the form. "Generate video" starts the run (`cartoon_real_world.py`).
// The draft is the server's (`cartoon-real-world-draft.ts`).
import { Group, Loader, Stack, Text, Textarea, Tooltip, UnstyledButton } from "@mantine/core";
import { AudioLines, Captions, Monitor, Smartphone } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { CartoonInputsResolution, CartoonScript, Job, Voice } from "../../../api/generated/model";
import {
  VideoTemplateQuote,
  VideoTemplateRequest,
  cartoonInputsBrandMax,
  cartoonInputsSizeMax,
  cartoonInputsSurfaceMax,
  type CartoonInputsOutput,
} from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { VoicePanel } from "../../../platform/voices/VoicePanel";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { CartoonRealWorldCast } from "./CartoonRealWorldCast";
import { ReuseSwitch, type TemplateFormProps } from "./kit";
import { InputChip, ScriptSection } from "./PodcastScript";
import { Submit } from "./staged";
import { StreetProductTile, type ProductWords } from "./StreetTiles";
import {
  SHOTS,
  blankScript,
  scriptProblems,
  useCartoonDraft,
  videoBody,
  type DraftWork,
  type VideoSettings,
} from "./cartoon-real-world-draft";
import classes from "./podcast.module.css";

const PRODUCT_WORDS: ProductWords = {
  tile:
    "The product the creature shows off and the room's hero shot ends on. Pick one of your products and its " +
    "packshot, or upload a packshot: every product image is drawn from it.",
  name: "The product's name. Leave empty and it is read off the packshot.",
  text: "A description or the product page. The script states only what it says.",
};
const SCRIPT_TOOLTIP =
  "Eleven lines, one a shot: the creature, the narrator and the woman. About 80 to 120 words in all, a line " +
  "at most 15 words, ending on the narrator's close with the product's name.";
const CAST_TOOLTIP =
  "The names, the five locks every image and clip is drawn with, the creature's voice and the hero's super. " +
  "Each color lock needs its hex colors (#RRGGBB).";
const SHOTS_TOOLTIP = "What happens in each of the eleven shots, and its camera.";
const VOICE_TOOLTIP =
  "The narrator's voice: a warm, wry adult American announcer, as the playbook has it. Listen in Voice " +
  "Settings and pick one.";
const SUBTITLES_TOOLTIP = "White subtitles on a dark box at the bottom, in the script's spelling.";
const RESOLUTION_TOOLTIP = "Resolution of every clip. 480p costs less than 720p.";
const FORMAT_TOOLTIP = "Every image and clip is vertical 9:16.";
const PICK_PRODUCT = "Pick a product and its packshot";
const GENERATE_SCRIPT = "Generate the script";
const FIX_CAST = "Fix the fields marked under The cast and the product";
const FIX_SHOTS = "Fix the shots' words";
const FIX_LINES = "Fix the script's lines";
const STILLS_PENDING = "Wait for the sixteen images";
const PICK_VOICE = "Pick a voice for the narrator";
const UNREADABLE_PACK = "The pack was hard to read: check the product's name and text.";
const HANDOFF_NOTICE = "Your Cartoon character ad is finishing in My Generations: you can close this panel now.";
const RESOLUTIONS: readonly CartoonInputsResolution[] = ["720p", "480p"];
const SPEAKER_NAMES = { CREATURE: "Creature", WOMAN: "Woman", NARRATOR: "Narrator" } as const;
/** About 3.5 words a second in a shot of at most 4.5 s. */
const MAX_LINE_WORDS = 15;
const CHIPS = [
  {
    field: "brand",
    label: "Brand",
    placeholder: "Everyday Dose (optional)",
    tooltip: "The brand as it is said. Leave empty and the director takes it from the product.",
    maxLength: cartoonInputsBrandMax,
  },
  {
    field: "size",
    label: "Size",
    placeholder: "A 16 oz bottle (optional)",
    tooltip: "The product's true size, so the creature is drawn to scale. Leave empty and it is left out.",
    maxLength: cartoonInputsSizeMax,
  },
  {
    field: "surface",
    label: "Surface",
    placeholder: "A kitchen counter (optional)",
    tooltip: "The surface the creature lives on. Leave empty and the director picks it.",
    maxLength: cartoonInputsSurfaceMax,
  },
] as const;
type CastField = Exclude<keyof CartoonScript, "lines" | "shots" | "pronunciations">;
/** The cast's, the locks' and the super's words the member may edit before approving. */
const CAST_FIELDS: readonly { field: CastField; label: string }[] = [
  { field: "creature_name", label: "Creature's name" },
  { field: "woman_name", label: "Woman's name" },
  { field: "product_short", label: "Product's short phrase" },
  { field: "mixed_media_lock", label: "Mixed-media lock" },
  { field: "creature_lock", label: "Creature lock" },
  { field: "woman_lock", label: "Woman lock" },
  { field: "product_lock", label: "Product lock" },
  { field: "location_lock", label: "Location lock" },
  { field: "clean_lock", label: "Clean surface lock" },
  { field: "creature_voice", label: "Creature's voice" },
  { field: "super_name", label: "Super's name" },
  { field: "super_line", label: "Super's line" },
];

function words(text: string): number {
  return text.split(/\s+/u).filter((each) => each !== "").length;
}

function progressOf(work: DraftWork, drawn: number, total: number): string | null {
  if (work.reading) return "Reading the pack";
  if (work.writing) return "Writing the script";
  return work.drawing ? `Generating images · ${drawn}/${total}` : null;
}

/** The cast's, the locks' and the super's words, folded under the script until opened. */
function CastFields(props: {
  script: CartoonScript;
  problems: Partial<Record<keyof CartoonScript, string>>;
  locked: boolean;
  onChange: (script: CartoonScript) => void;
}) {
  const { script, problems, locked, onChange } = props;
  const refused = CAST_FIELDS.some(({ field }) => problems[field] !== undefined);
  return (
    <details className={classes.chips} open={refused || undefined}>
      <summary>The cast and the product</summary>
      <Stack gap={8}>
        <Text size="xs">{CAST_TOOLTIP}</Text>
        {CAST_FIELDS.map(({ field, label }) => (
          <Textarea
            key={field}
            label={label}
            autosize
            minRows={1}
            value={script[field]}
            error={problems[field]}
            disabled={locked}
            onChange={(event) => onChange({ ...script, [field]: event.currentTarget.value })}
          />
        ))}
      </Stack>
    </details>
  );
}

/** The eleven shots' actions and cameras, folded under the cast until opened. */
function ShotFields(props: {
  script: CartoonScript;
  problem: string | undefined;
  locked: boolean;
  onChange: (script: CartoonScript) => void;
}) {
  const { script, problem, locked, onChange } = props;
  const change = (at: number, part: Partial<CartoonScript["shots"][number]>) =>
    onChange({ ...script, shots: script.shots.map((shot, each) => (each === at ? { ...shot, ...part } : shot)) });
  return (
    <details className={classes.chips} open={problem !== undefined || undefined}>
      <summary>The eleven shots</summary>
      <Stack gap={8}>
        <Text size="xs">{SHOTS_TOOLTIP}</Text>
        {problem === undefined ? null : (
          <Text size="xs" c="red" role="alert">
            {problem}
          </Text>
        )}
        {script.shots.map((shot, at) => (
          <Stack key={SHOTS[at] ?? at} gap={4}>
            <Textarea
              label={`Shot ${at + 1}`}
              autosize
              minRows={1}
              value={shot.action}
              disabled={locked}
              onChange={(event) => change(at, { action: event.currentTarget.value })}
            />
            <Textarea
              aria-label={`Shot ${at + 1}'s camera`}
              placeholder="Camera"
              autosize
              minRows={1}
              value={shot.camera}
              disabled={locked}
              onChange={(event) => change(at, { camera: event.currentTarget.value })}
            />
          </Stack>
        ))}
      </Stack>
    </details>
  );
}

/** The voice the narrator speaks with, as Voice Settings hands it over. */
type VoiceChoice = Pick<Voice, "id" | "name" | "kind">;

/**
 * The form: the stage, and in the footer the narrator's voice, the subtitles, the resolution, the
 * 9:16 format and the round start, "Approve script" then "Generate video", whose tooltip says what
 * it waits for. The voice, the subtitles and the resolution stay editable after Approve script.
 */
export function CartoonRealWorldForm(props: TemplateFormProps) {
  const { teamId, brandId, above, onStarted, example } = props;
  const [collapsed, setCollapsed] = useState(false);
  const flow = useCartoonDraft(teamId, brandId);
  const [settings, setSettings] = useState<VideoSettings | null>(null);
  const [voice, setVoice] = useState<VoiceChoice | null>(null);
  const [voicePanel, setVoicePanel] = useState(false);
  const [productAsked, setProductAsked] = useState(false);
  const [started, setStarted] = useState(false);
  const { form, draft } = flow;
  const approved = draft !== undefined && draft.stage !== "script";
  const shownSettings: VideoSettings | null =
    form === null
      ? null
      : ((approved ? settings : null) ?? { subtitles: form.inputs.subtitles, resolution: form.inputs.resolution });
  const video = draft === undefined || shownSettings === null ? null : videoBody(draft, shownSettings, voice?.id ?? null);
  const videoRequest = VideoTemplateRequest.safeParse(video);
  const videoQuote = VideoTemplateQuote.safeParse(video);
  const videoStart = usePricedStart({
    teamId,
    brandId,
    quote: videoQuote.success ? videoQuote.data : undefined,
    request: videoRequest.success ? videoRequest.data : undefined,
    onStarted: (job: Job) => {
      setStarted(true);
      onStarted(job);
    },
  });
  if (!approved && settings !== null) setSettings(null);

  const panel = (body: ReactNode, footer?: ReactNode) => (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">Cartoon character in the real world</Text>}
      footer={footer}
      aside={
        voicePanel ? (
          <VoicePanel
            teamId={teamId}
            brandId={brandId}
            chosen={voice}
            onChoose={setVoice}
            onClose={() => setVoicePanel(false)}
            designModel={undefined}
            only={["global", "team"]}
          />
        ) : undefined
      }
    >
      {body}
    </GlassPanel>
  );
  if (flow.read.isError) return panel(<ErrorAlert error={flow.read.error} />);
  if (draft === undefined || form === null || shownSettings === null) {
    return panel(<Loader size="sm" aria-label="Loading the draft" />);
  }

  const { inputs, script } = form;
  const problems = scriptProblems(form);
  const setInputs = (change: Partial<CartoonInputsOutput>) => flow.editInputs(change);
  const changeSettings = (change: Partial<VideoSettings>) => {
    if (approved) setSettings({ ...shownSettings, ...change });
    else setInputs(change);
  };
  const locked = !flow.editable;
  const work = flow.work;
  const writing = work.reading || work.writing;
  const drawn = draft.stills.filter((each) => each.status === "done").length;
  const progress = progressOf(work, drawn, draft.stills.length);

  let submit;
  if (approved) {
    const ready = draft.stills.length > 0 && drawn === draft.stills.length;
    const reasons = [...(ready ? [] : [STILLS_PENDING]), ...(voice === null ? [PICK_VOICE] : [])];
    submit = (
      <Submit
        label="Generate video"
        reasons={reasons}
        disabled={reasons.length > 0 || flow.busy || !videoStart.canStart || videoStart.start.isPending}
        loading={videoStart.start.isPending}
        onClick={videoStart.startJob}
      />
    );
  } else {
    const reasons = [
      ...(inputs.product ? [] : [PICK_PRODUCT]),
      ...(script === null ? [GENERATE_SCRIPT] : []),
      ...(CAST_FIELDS.some(({ field }) => problems[field] !== undefined) ? [FIX_CAST] : []),
      ...(problems.shots === undefined ? [] : [FIX_SHOTS]),
      ...(problems.lines === undefined ? [] : [FIX_LINES]),
    ];
    submit = (
      <Submit
        label="Approve script"
        reasons={reasons}
        disabled={reasons.length > 0 || flow.busy}
        loading={flow.approving}
        onClick={flow.approve}
      />
    );
  }

  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <div className={classes.settings}>
        <Tooltip label={VOICE_TOOLTIP} multiline maw={280} withArrow>
          <UnstyledButton
            className={classes.setting}
            data-on={voice !== null || undefined}
            aria-label={voice === null ? "Narrator's voice" : `Narrator's voice: ${voice.name}`}
            onClick={() => setVoicePanel(true)}
          >
            <AudioLines size={16} strokeWidth={1.5} aria-hidden />
            {voice === null ? "Narrator's voice" : voice.name}
          </UnstyledButton>
        </Tooltip>
        <Tooltip label={SUBTITLES_TOOLTIP} multiline maw={280} withArrow>
          <UnstyledButton
            className={classes.setting}
            data-on={shownSettings.subtitles || undefined}
            aria-pressed={shownSettings.subtitles}
            disabled={flow.busy}
            onClick={() => changeSettings({ subtitles: !shownSettings.subtitles })}
          >
            <Captions size={16} strokeWidth={1.5} aria-hidden />
            {shownSettings.subtitles ? "Subtitles on" : "Subtitles off"}
          </UnstyledButton>
        </Tooltip>
        <Choice
          variant="footer"
          setting="Resolution"
          icon={<Monitor size={16} strokeWidth={1.5} />}
          tip={RESOLUTION_TOOLTIP}
          width={88}
          value={shownSettings.resolution}
          options={RESOLUTIONS.map((value) => ({ value, label: value }))}
          disabled={flow.busy}
          onChange={(next) => {
            const resolution = RESOLUTIONS.find((each) => each === next);
            if (resolution !== undefined) changeSettings({ resolution });
          }}
        />
        <Tooltip label={FORMAT_TOOLTIP} multiline maw={280} withArrow>
          <span className={classes.setting}>
            <Smartphone size={16} strokeWidth={1.5} aria-hidden />
            9:16
          </span>
        </Tooltip>
      </div>
      {submit}
    </Group>
  );

  const refused = videoStart.start.error ?? videoStart.quoteError ?? flow.failure;
  const status = (
    <>
      {draft.problem === null ? null : (
        <Text className={classes.problem} role="alert">
          {draft.problem}
        </Text>
      )}
      {refused === null ? null : <ErrorAlert error={refused} />}
      {progress === null ? null : (
        <output className={classes.progress}>
          <Loader size={14} aria-hidden />
          {progress}
        </output>
      )}
      {started && approved ? (
        <Text component="output" size="sm">
          Generation started. {HANDOFF_NOTICE}
        </Text>
      ) : null}
    </>
  );
  if (approved) {
    return panel(
      <div className={classes.content}>
        <CartoonRealWorldCast
          stills={draft.stills}
          busy={flow.busy}
          resetting={flow.resetting}
          onRegenerate={flow.regenerate}
          onStartOver={() => {
            setStarted(false);
            flow.startOver();
          }}
        />
        {status}
      </div>,
      footer,
    );
  }

  const warnings = [...(draft.pack_warning ? [UNREADABLE_PACK] : []), ...draft.script_problems];
  const total = script === null ? 0 : script.lines.reduce((sum, line) => sum + words(line.text), 0);
  const body = (
    <div className={classes.content}>
      <Stack gap={12}>
        <div className={classes.tiles}>
          <StreetProductTile
            teamId={teamId}
            brandId={brandId}
            pick={inputs.product ?? null}
            typed={{ product_name: inputs.product_name, product_text: inputs.product_text }}
            disabled={locked}
            missing={productAsked}
            exampleUrls={example?.urls ?? {}}
            words={PRODUCT_WORDS}
            onChange={(product, typed) => setInputs({ product, ...typed, product_category: "" })}
          />
        </div>
        <div className={classes.chips}>
          {CHIPS.map((chip) => (
            <InputChip
              key={chip.field}
              label={chip.label}
              value={inputs[chip.field]}
              placeholder={chip.placeholder}
              tooltip={chip.tooltip}
              maxLength={chip.maxLength}
              disabled={locked}
              onChange={(value) => setInputs({ [chip.field]: value })}
            />
          ))}
        </div>
        <ScriptSection
          lines={
            script === null
              ? null
              : script.lines.map((line, at) => ({
                  id: at + 1,
                  speaker: SPEAKER_NAMES[line.speaker],
                  beat: `Shot ${at + 1}`,
                  text: line.text,
                }))
          }
          meta={script === null ? null : `${script.lines.length} lines · ${total} words (80–120)`}
          tooltip={SCRIPT_TOOLTIP}
          primary={SPEAKER_NAMES.CREATURE}
          maxWords={() => MAX_LINE_WORDS}
          hint={{
            wand: "to write the eleven lines, the shots and the cast from the product, or",
            pencil: "to write them yourself.",
          }}
          writeLabel="Write the eleven lines yourself"
          writing={writing}
          locked={locked}
          issue={null}
          warning={warnings.length === 0 ? null : warnings.join(" ")}
          onGenerate={() => {
            if (!inputs.product) {
              setProductAsked(true);
              return;
            }
            flow.generateScript();
          }}
          onWrite={() => flow.edit({ script: blankScript() })}
          onLine={(index, text) => {
            if (script === null) return;
            const lines = script.lines.map((line, at) => (at === index ? { ...line, text } : line));
            flow.edit({ script: { ...script, lines } });
          }}
        />
        {script === null ? null : (
          <>
            <ShotFields
              script={script}
              problem={problems.shots}
              locked={locked}
              onChange={(next) => flow.edit({ script: next })}
            />
            <CastFields
              script={script}
              problems={problems}
              locked={locked}
              onChange={(next) => flow.edit({ script: next })}
            />
          </>
        )}
      </Stack>
      {status}
    </div>
  );
  return panel(body, footer);
}
